/* engine/surfaces.js — THE oracle for WORKTOPS and BACKSPLASHES (2026-08-15).
 *
 * WHY THIS EXISTS
 * Worktops used to be derived twice, differently: engine/worktopRuns.js drove
 * 3D + drawings + Present, while engine/outputs.js getWorktopRuns drove the
 * quote payload with a different gap tolerance, different eligibility and a
 * mitre model the 3D never saw. The backsplash had no run model at all — it
 * was emitted per module, so an 8-cabinet wall rendered 8 abutting panels.
 * This module is the single source every surface consumes.
 *
 * THE MODEL
 * A surface is a RUN along one wall: a contiguous band of base cabinets that
 * share a material and a height band. Runs are DERIVED — they re-flow when a
 * cabinet moves — while the designer's edits ride separately, keyed by a
 * STABLE identity (see surfaceKey), so an edit survives adding, removing and
 * re-ordering cabinets.
 *
 * GEOMETRY comes from the technician material (thickness, overhangs, splash
 * height, sheet length) with engine defaults when a field is absent, so a
 * catalog authored before today behaves exactly as it did.
 *
 * Pure module: no THREE, no state mutation, metres in / metres out.
 */
import { WALL_THICKNESS } from "./constants";
import { isKitchenDef } from "./families";

/* ── engine defaults: the values hardcoded across the codebase before the
 * technician could author them. Changing these changes nothing for a catalog
 * that fills the fields in. ─────────────────────────────────────────────── */
export const DEF_WORKTOP_T_MM = 38;        // cabinetMath's literal 0.038
export const DEF_OVERHANG_FRONT_MM = 20;   // cabinetMath's slabD = depth + 0.020
export const DEF_OVERHANG_SIDE_MM = 0;     // flush ends
export const DEF_SPLASH_T_MM = 12;         // cabinetMath's 0.012 panel
export const DEF_SPLASH_H_MM = 550;        // cabinetMath's 0.55
export const DEF_SHEET_MM = 4100;
export const RUN_GAP_M = 0.005;            // worktopRuns.RUN_GAP_M — unchanged
export const EDGE_PROFILES = ["square", "bullnose", "bevel", "mitred", "postformed"];

const num = (v, dflt) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : dflt;
};

/** The technician entry for a worktop/backsplash NAME (the app-wide id). */
export function surfaceMaterial(blob, kind, name) {
  if (!name || name === "None") return null;
  const list = ((blob || {})[kind === "splash" ? "backsplash" : "worktops"]) || [];
  return list.find((x) => x && x.name === name) || null;
}

/** Resolved spec for a material: technician value → engine default.
 *  `over` is the room/run override layer (already merged by the caller). */
export function surfaceSpecOf(mat, kind, over) {
  const o = over || {};
  const m = mat || {};
  if (kind === "splash") {
    return {
      thicknessMm: num(o.thicknessMm, num(m.thicknessMm, DEF_SPLASH_T_MM)),
      heightMm: num(o.heightMm, num(m.heightMm, DEF_SPLASH_H_MM)),
      pricePerM2: num(m.pricePerM2, 0),
      price: num(m.price, 0),
    };
  }
  return {
    thicknessMm: num(o.thicknessMm, num(m.thicknessMm, DEF_WORKTOP_T_MM)),
    overhangFrontMm: num(o.overhangFrontMm, num(m.overhangFrontMm, DEF_OVERHANG_FRONT_MM)),
    // BACK overhang (2026-08-31): override-only — no material default, so
    // every existing kitchen keeps depth byte-identical until a run is edited
    overhangBackMm: num(o.overhangBackMm, 0),
    overhangSideMm: num(o.overhangSideMm, num(m.overhangSideMm, DEF_OVERHANG_SIDE_MM)),
    // PER-END wings (2026-08-31 user: maths in Side overhang pops Left/Right
    // — each end its own value); absent -> follow the symmetric field
    overhangSideLMm: num(o.overhangSideLMm, num(o.overhangSideMm, num(m.overhangSideMm, DEF_OVERHANG_SIDE_MM))),
    overhangSideRMm: num(o.overhangSideRMm, num(o.overhangSideMm, num(m.overhangSideMm, DEF_OVERHANG_SIDE_MM))),
    edge: o.edge || m.edge || "square",
    sheetMm: num(m.maxLenMm, DEF_SHEET_MM) >= 300 ? num(m.maxLenMm, DEF_SHEET_MM) : DEF_SHEET_MM,
    pricePerM2: num(m.pricePerM2, 0),
    edgePricePerM: num(m.edgePricePerM, 0),
    jointPrice: num(m.jointPrice, 0),
    cutoutPrice: num(m.cutoutPrice, 0),
    price: num(m.price, 0),
  };
}

/** Seam positions inside [s0,s1], every sheet length from the run start.
 *  (Same naive stride as the previous engine — deliberately unchanged so
 *  existing drawings keep their seam ticks in the same places.) */
export function runSeams(s0, s1, maxLenM) {
  const out = [];
  if (!(maxLenM > 0.05)) return out;
  for (let s = s0 + maxLenM; s < s1 - 0.01; s += maxLenM) out.push(s);
  return out;
}

/* ── STABLE IDENTITY ───────────────────────────────────────────────────────
 * Positional ids ("__wtrun__0") break the moment a cabinet is added. A run is
 * identified by its wall and its FIRST member — stable while that cabinet
 * lives. When it does not survive, resolveOverride re-anchors by member
 * overlap, so an edit follows the run it was made on. */
export function surfaceKey(kind, wallId, members) {
  const first = (members && members.length) ? String(members[0]) : "?";
  return (kind === "splash" ? "sp" : "wt") + ":" + String(wallId || "-") + ":" + first;
}

/** Pick the override for a run. Exact key wins only while the stored member
 *  list still covers ≥ half of the run (Jaccard ≥ 0.5) — a run that shrank
 *  from 3 cabinets to 1 is not the run the edit was made on, even though its
 *  first member (the key) survived (2026-09-12: a 2.4 m panel on a 600 mm
 *  cabinet). A legacy entry with no members list stays exact-key only. Else
 *  the best member overlap ≥ 0.5, never across kinds.
 *  Returns { key, over, score, exact } or null. */
export function resolveOverrideKey(store, key, members) {
  if (!store) return null;
  const mine = new Set(members || []);
  const kind = key.split(":")[0];
  const scoreOf = (o) => {
    const theirs = (o && o.members) || [];
    if (!theirs.length) return 0;
    let inter = 0;
    theirs.forEach((id) => { if (mine.has(id)) inter += 1; });
    return inter ? inter / (mine.size + theirs.length - inter) : 0;
  };
  const ex = store[key];
  if (ex) {
    if (!((ex.members || []).length)) return { key, over: ex, score: 1, exact: true };
    const s = scoreOf(ex);
    if (s >= 0.5) return { key, over: ex, score: s, exact: true };
  }
  if (!mine.size) return null;
  let best = null, bestKey = null, bestScore = 0;
  Object.keys(store).forEach((k) => {
    const o = store[k];
    if (!o || k === key || k.split(":")[0] !== kind) return;   // never cross kinds
    const s = scoreOf(o);
    if (s > bestScore) { bestScore = s; best = o; bestKey = k; }
  });
  return bestScore >= 0.5 ? { key: bestKey, over: best, score: bestScore, exact: false } : null;
}
export function resolveOverride(store, key, members) {
  const r = resolveOverrideKey(store, key, members);
  return r ? r.over : null;
}

/* ── the derive ───────────────────────────────────────────────────────────
 * buildSurfaces({ placements, lib, blob, wizard, effDef, geomOf, soloOf })
 *   geomOf(p, def) → { s, q, angle, w, topM, depthM } in the wall's axis
 *                    (identical contract to golaRuns — the callers already
 *                    compute it from the module's own carcass part)
 *   soloOf(p)      → true when the cabinet must keep its own slab (CAD cuts,
 *                    a GLB that owns its worktop)
 * Returns { worktops: [Surface], splashes: [Surface] }. */
export function buildSurfaces(opts) {
  const { placements, lib, blob, wizard, effDef, geomOf, soloOf } = opts || {};
  const kw = wizard || {};
  const store = kw.surfaceOverrides || null;
  // RESOLUTION CHAIN (2026-08-18 user mandate "the mm comes from the Cabinet
  // Technician"): technician material -> per-run override (elevation drag).
  // The old Room-step spec layer is GONE - the Room step now only PICKS the
  // material and shows the technician numbers read-only.

  const items = [];
  const tallZones = [];
  (placements || []).forEach((p) => {
    if (!p || !p.wallId || p.island) return;
    const def = effDef ? effDef(p, lib) : ((lib || {})[p.type]);
    if (def && !isKitchenDef(def)) return;          // furniture lines carry no worktop / splash (engine/families)
    if (def && def.role === "cabinet-tall") {
      // a tall unit never carries a splash and nothing may stand in front of
      // it: its span is a forbidden zone for every panel on this wall face
      // (2026-09-12 user: "the backsplash goes outside of the wall")
      const gt = geomOf ? geomOf(p, def) : null;
      if (gt) tallZones.push({ wallId: p.wallId, s0: gt.s - gt.w / 2, s1: gt.s + gt.w / 2, fs: p.faceSign === -1 ? -1 : 1 });
      return;
    }
    // appliances CARRY the worktop too (2026-08-18): the slab runs over a
    // dishwasher and the splash runs behind it — excluding them split runs
    // at every slot appliance. Insert appliances (hob/sink) overlay a base
    // unit, and the interval-union chaining absorbs the overlap.
    if (!def || (def.role !== "cabinet-base" && def.role !== "appliance")) return;
    const g = geomOf ? geomOf(p, def) : null;
    if (!g) return;
    const cfg = p.config || {};
    items.push({
      id: p.id, wallId: p.wallId,
      s0: g.s - g.w / 2, s1: g.s + g.w / 2,
      q: g.q, angle: g.angle || 0,
      topM: g.topM, depthM: g.depthM,
      solo: !!(soloOf && soloOf(p)),
      wtName: String(cfg.countertop || ""),
      bsName: String(cfg.backsplash || ""),
      // index 0 is the FIRST equipment item — a plain truthiness test read it
      // as "no sink" (found while porting to Python; both sides now agree)
      hasSink: cfg.eqSinks != null && cfg.eqSinks !== -1 && cfg.eqSinks !== "",
      hasHob: cfg.eqHobs != null && cfg.eqHobs !== -1 && cfg.eqHobs !== "",
      /* SPLASH ELIGIBILITY facts (2026-08-29 user: "why backsplash being put
       * on islands — backsplash only puts on the wall, make it smart").
       * Read from the PLACEMENT, never from the caller's axis: the elevation
       * caller flattens q to 0, so any q-based rule would disagree between
       * views. faceSign tells which face of a double-sided wall the module
       * stands on; outset (mm) is how far it was pulled off the wall. */
      fs: p.faceSign === -1 ? -1 : 1,
      outsetMm: Math.abs((cfg.position && cfg.position.outset) || 0),
    });
  });

  /* ── THE BACKSPLASH SMART RULE (2026-08-29) ──────────────────────────────
   * A backsplash exists only where a REAL wall stands behind the module's
   * back. Three ways that fails, all seen in the wild:
   *   · the module is an island (no wall at all — filtered at collection);
   *   · the module was pulled off the wall (outset) — the panel would float
   *     in the air behind it;
   *   · the module stands on a partition wall with another run BACK-TO-BACK
   *     on the opposite face — that is how islands are built here (his
   *     "island" is base units on both faces of a wall stub), and a splash
   *     fin standing between two back-to-back runs is never right. The 3D
   *     dollhouse hides the partition itself, so the panel even renders
   *     free-floating.
   *   · the module stands on a PENINSULA guide wall (style "peninsula") —
   *     an imaginary line with no body at all (2026-09-12).
   * A single-face partition keeps its splash: the wall is visibly there.
   * Worktops are untouched — an island NEEDS its worktop. */
  /* NO WALL BODY = NO SPLASH (2026-09-12 user: "FIX THE BACKSPLASH IT GOES TO
   * THE ISLAND AGAIN"). A style "peninsula" wall is an imaginary guide line —
   * roomMath gives it no body and no view ever draws it — and it is how a
   * free-standing island is laid out here: base units on ONE face of that
   * guide. They carry a wallId (not the island flag) and nobody stands
   * back-to-back, so the 2026-08-29 rule kept their splash: a panel standing
   * in the air behind the island. A peninsula guide never backs a splash;
   * worktops are untouched (the island NEEDS its slab). Read from opts.walls,
   * the list every caller already passes for the corner joints. */
  const noBodyWall = new Set();
  ((opts && opts.walls) || []).forEach((w) => {
    if (w && w.id != null && String(w.style || "standard") === "peninsula") noBodyWall.add(w.id);
  });
  items.forEach((it) => {
    it.splashOk = it.outsetMm <= 50 && !noBodyWall.has(it.wallId);
    if (!it.splashOk) return;
    for (const ot of items) {
      if (ot === it || ot.wallId !== it.wallId || ot.fs === it.fs) continue;
      if (Math.min(it.s1, ot.s1) - Math.max(it.s0, ot.s0) > 0.01) { it.splashOk = false; break; }
    }
  });
  /* FORBIDDEN ZONES + the eligibility record (2026-08-29 adversarial review).
   * Suppressing an item from the GROUPING was not enough: a stored elevation
   * drag-override re-applies its old span in mkSplash, and connectCorners can
   * snap a neighbouring run's end back across the island — both rebuilt the
   * exact floating fin the rule removes. So the ineligible spans are recorded
   * per wall and every splash is CLIPPED against them as the very last step,
   * whatever route tried to stretch it there. splashEligible rides the result
   * so the pricers can drop per-module flat charges for panels that no longer
   * exist. */
  const forbiddenByWall = new Map();
  const splashEligible = {};
  items.forEach((it) => {
    splashEligible[it.id] = !!it.splashOk;
    if (it.splashOk) return;
    if (!forbiddenByWall.has(it.wallId)) forbiddenByWall.set(it.wallId, []);
    forbiddenByWall.get(it.wallId).push([it.s0, it.s1]);
  });
  /* TALL UNITS are forbidden ground too (2026-09-12): per wall AND face, so
   * a tall unit on a partition's other face never clips */
  const tallByWall = new Map();
  tallZones.forEach((z) => { if (!tallByWall.has(z.wallId)) tallByWall.set(z.wallId, []); tallByWall.get(z.wallId).push(z); });
  /* subtract the wall's forbidden intervals from [s0,s1]; keep the LARGEST
   * surviving piece (a run never straddles an island), null when nothing
   * usable is left */
  const clipForbidden = (wallId, s0, s1, fs) => {
    const fb = [
      ...(forbiddenByWall.get(wallId) || []),
      ...((tallByWall.get(wallId) || []).filter((z) => z.fs === fs).map((z) => [z.s0, z.s1])),
    ];
    if (!fb.length || s1 - s0 <= 0) return [s0, s1];
    let segs = [[s0, s1]];
    fb.forEach(([f0, f1]) => {
      const next = [];
      segs.forEach(([a, b2]) => {
        if (f1 <= a + 0.001 || f0 >= b2 - 0.001) { next.push([a, b2]); return; }
        if (f0 > a) next.push([a, f0]);
        if (f1 < b2) next.push([f1, b2]);
      });
      segs = next;
    });
    let best = null;
    segs.forEach((sg) => { if (!best || sg[1] - sg[0] > best[1] - best[0]) best = sg; });
    return best && best[1] - best[0] >= 0.05 ? best : null;
  };

  /* CAD-LEVEL BANDING (2026-08-18 user mandate: "units are big and small —
   * make the worktop and backsplash CAD-accurate").
   *  · TOLERANCE: heights quantize to 3mm buckets so an imported 780.4mm unit
   *    still joins the 780mm run instead of fragmenting it.
   *  · DEPTH-MERGE: depth and wall distance are NOT part of the band — a
   *    570/450/570 row is ONE slab whose depth follows the DEEPEST member
   *    (one straight front line, back at the wall), exactly like real CAD.
   *    Only the FACE SIGN survives (a partition wall's two faces never merge).
   *  · allowEmpty: worktops merge even with no material chosen (the engine
   *    default slab is real geometry); a backsplash stays opt-in. */
  const Q3 = (v) => Math.round(v / 0.003);
  const group = (pick, gOpts) => {
    const allowEmpty = !!(gOpts && gOpts.allowEmpty);
    const buckets = new Map();
    items.forEach((it) => {
      const name = pick(it);
      if (name === "None") return;
      if (!name && !allowEmpty) return;                        // nothing chosen → no surface
      const face = it.q >= 0 ? "+" : "-";
      const bandKey = [it.wallId, name, Q3(it.topM), face, it.solo ? it.id : ""].join("|");
      if (!buckets.has(bandKey)) buckets.set(bandKey, []);
      buckets.get(bandKey).push(it);
    });
    const runs = [];
    buckets.forEach((arr) => {
      arr.sort((a, b) => a.s0 - b.s0);
      let cur = null;
      const flush = () => { if (cur) runs.push(cur); cur = null; };
      arr.forEach((it) => {
        if (cur && it.s0 - cur.s1 <= RUN_GAP_M) {
          cur.s1 = Math.max(cur.s1, it.s1);
          cur.members.push(it.id);
          cur.cutouts += (it.hasSink ? 1 : 0) + (it.hasHob ? 1 : 0);
          // the DEEPEST member drives the slab: depth + wall distance
          if (it.depthM > cur.depthM + 1e-9) { cur.depthM = it.depthM; cur.q = it.q; }
        } else {
          flush();
          cur = {
            wallId: it.wallId, name: pick(it), q: it.q, angle: it.angle,
            topM: it.topM, depthM: it.depthM, s0: it.s0, s1: it.s1,
            fs: it.fs,
            members: [it.id], cutouts: (it.hasSink ? 1 : 0) + (it.hasHob ? 1 : 0),
          };
        }
      });
      flush();
    });
    runs.sort((a, b) => (a.wallId < b.wallId ? -1 : a.wallId > b.wallId ? 1 : a.s0 - b.s0));
    return runs;
  };

  const mkWorktop = (r) => {
    const key = surfaceKey("worktop", r.wallId, r.members);
    const over = resolveOverride(store, key, r.members) || {};
    const mat = surfaceMaterial(blob, "worktop", r.name);
    const spec = surfaceSpecOf(mat, "worktop", over);
    const s0 = num(over.s0, r.s0), s1 = num(over.s1, r.s1);
    return {
      kind: "worktop", key, auto: !Object.keys(over).length,
      wallId: r.wallId, angle: r.angle, q: r.q,
      s0, s1, lengthM: Math.round((s1 - s0) * 1000) / 1000,
      members: r.members.slice(),
      material: r.name, colourIdx: num(over.colourIdx, 0),
      thicknessM: spec.thicknessMm / 1000,
      overhangFrontM: spec.overhangFrontMm / 1000,
      overhangSideM: spec.overhangSideMm / 1000,
      overhangSideLM: spec.overhangSideLMm / 1000,
      overhangSideRM: spec.overhangSideRMm / 1000,
      overhangBackM: spec.overhangBackMm / 1000,
      // forward/back POSITION slide (2026-08-31, override-only, render + record)
      qOffM: num(over.qOffMm, 0) / 1000,
      edge: spec.edge,
      depthM: r.depthM + spec.overhangFrontMm / 1000 + spec.overhangBackMm / 1000,
      topM: r.topM,                                   // slab BOTTOM sits here
      seams: runSeams(s0, s1, spec.sheetMm / 1000),
      cutouts: r.cutouts,
      // CAD facts (2026-08-18): what each end meets + registered joints
      ends: r.ends || { s0: "free", s1: "free" },
      cornerButt: r.cornerButt || null,
      cornerJoints: r.cornerJoints || 0,
      stepJoints: r.stepJoints || 0,
      stepDropM: r.stepDropM || 0,
      spec,
    };
  };

  const mkSplash = (r) => {
    const key = surfaceKey("splash", r.wallId, r.members);
    const over = r.over || {};                            // resolved + claimed once, below
    const mat = surfaceMaterial(blob, "splash", r.name);
    const spec = surfaceSpecOf(mat, "splash", over);
    /* the span already carries the override, the wall clamp and the opening
       cuts; the FORBIDDEN clip (islands + tall units) has the last word — a
       stale pre-rule drag (or a fresh one into the island zone) can never
       stand a panel where no wall backs it */
    const clip = clipForbidden(r.wallId, r.s0, r.s1, r.fs);
    if (!clip) return null;
    const s0 = clip[0], s1 = clip[1];
    // the splash starts on TOP of the slab when one is there, else at the
    // carcass top — resolved by the caller through worktopTopFor()
    return {
      kind: "splash", key, auto: !Object.keys(over).length,
      wallId: r.wallId, angle: r.angle, q: r.q,
      s0, s1, lengthM: Math.round((s1 - s0) * 1000) / 1000,
      members: r.members.slice(),
      material: r.name, colourIdx: num(over.colourIdx, 0),
      thicknessM: spec.thicknessMm / 1000,
      heightM: spec.heightMm / 1000,
      depthM: r.depthM,
      topM: r.topM,
      ends: r.ends || { s0: "free", s1: "free" },
      spec,
    };
  };

  /* ── WALL GEOMETRY for corners + end classification ─────────────────────── */
  const wallList = (opts && opts.walls) || [];
  const wallsById = new Map();
  wallList.forEach((w) => {
    if (!w || !w.id || !Array.isArray(w.start) || !Array.isArray(w.end)) return;
    const dx = w.end[0] - w.start[0], dz = w.end[1] - w.start[1];
    const len = Math.hypot(dx, dz);
    if (!(len > 0.01)) return;
    wallsById.set(w.id, { w, len, dir: [dx / len, dz / len], t: num(w.thickness, WALL_THICKNESS) });
  });

  /* CORNER BUTT-JOINTS (CAD rule): at a shared wall corner the run reaching
   * closest to the junction is PRIMARY — it extends to the other wall's inner
   * face; the SECONDARY butts against the primary's edge (wall t/2 + primary
   * depth). Snapped ONLY within 0.30m of the targets, so an empty corner never
   * grows a floating slab. The primary registers one corner joint. */
  const SNAP = 0.30;
  const connectCorners = (runs, depthOfRun, follow) => {
    const firedOut = [];
    const pairs = [];
    const ids = [...wallsById.keys()];
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        const A = wallsById.get(ids[i]), B = wallsById.get(ids[j]);
        const dot = A.dir[0] * B.dir[0] + A.dir[1] * B.dir[1];
        if (Math.abs(dot) > 0.5) continue;                    // near-perpendicular only
        [[A.w.start, 0], [A.w.end, A.len]].forEach(([pa, sA]) => {
          [[B.w.start, 0], [B.w.end, B.len]].forEach(([pb, sB]) => {
            if (Math.hypot(pa[0] - pb[0], pa[1] - pb[1]) <= 0.005) {
              pairs.push({ a: ids[i], b: ids[j], sA, sB });
            }
          });
        });
      }
    }
    pairs.forEach((c) => {
      const A = wallsById.get(c.a), B = wallsById.get(c.b);
      // the run on each wall reaching closest to this junction
      const nearest = (wallId, cornerS, len) => {
        let best = null, bestD = Infinity;
        runs.forEach((r) => {
          if (r.wallId !== wallId) return;
          const d = cornerS === 0 ? r.s0 : len - r.s1;
          if (d >= -0.02 && d < bestD) { bestD = d; best = r; }
        });
        return best ? { run: best, d: bestD } : null;
      };
      const ra = nearest(c.a, c.sA, A.len), rb = nearest(c.b, c.sB, B.len);
      if (!ra || !rb) return;
      const aPrimary = ra.d < rb.d || (ra.d === rb.d && c.a <= c.b);
      const P = aPrimary ? ra : rb, S = aPrimary ? rb : ra;
      const wp = aPrimary ? A : B, ws = aPrimary ? B : A;
      const cornerP = aPrimary ? c.sA : c.sB, cornerSd = aPrimary ? c.sB : c.sA;
      const targetP = ws.t / 2;                               // to the other wall's inner face
      const targetS = wp.t / 2 + depthOfRun(P.run);           // butt on the primary's edge
      // the SPLASH follows the worktop around a corner it already proved real:
      // its return along the second wall is ~the slab depth, so its secondary
      // gets a wider snap zone (follow = the fired worktop corners).
      const fired = follow && follow.some((f) =>
        (f.a === c.a && f.b === c.b && f.sA === c.sA && f.sB === c.sB));
      const zoneS = fired ? SNAP + 0.70 : SNAP;
      if (Math.abs(P.d - targetP) > SNAP || Math.abs(S.d - targetS) > zoneS) return;
      const setEnd = (r, cornerS, len, dist) => {
        if (cornerS === 0) r.s0 = dist; else r.s1 = len - dist;
        r.cornerAdjusted = true;
        if (!r.ends) r.ends = {};
        r.ends[cornerS === 0 ? "s0" : "s1"] = "corner";
      };
      setEnd(P.run, cornerP, (aPrimary ? A : B).len, targetP);
      setEnd(S.run, cornerSd, (aPrimary ? B : A).len, targetS);
      // the BUTTED run carries the visible joint line (3D seam + drawings)
      S.run.cornerButt = cornerSd === 0 ? "s0" : "s1";
      P.run.cornerJoints = (P.run.cornerJoints || 0) + 1;
      firedOut.push({ a: c.a, b: c.b, sA: c.sA, sB: c.sB });
    });
    return firedOut;
  };

  /* HEIGHT STEPS: adjacent runs on the same wall at different heights meet in
   * a STEP — a registered joint; the TALLER slab's step side is a finished
   * (visible) end. */
  const markSteps = (runs) => {
    for (let i = 0; i < runs.length; i++) {
      for (let j = i + 1; j < runs.length; j++) {
        const a = runs[i], b = runs[j];
        if (a.wallId !== b.wallId || (a.q >= 0) !== (b.q >= 0)) continue;
        if (Math.abs(a.topM - b.topM) <= 0.003) continue;
        const [lo, hi] = a.s0 <= b.s0 ? [a, b] : [b, a];
        if (hi.s0 - lo.s1 > RUN_GAP_M + 0.001) continue;
        const taller = a.topM > b.topM ? a : b;
        if (!lo.ends) lo.ends = {};
        if (!hi.ends) hi.ends = {};
        lo.ends.s1 = "step";
        hi.ends.s0 = "step";
        taller.stepJoints = (taller.stepJoints || 0) + 1;
        taller.stepDropM = Math.round(Math.abs(a.topM - b.topM) * 1000) / 1000;
      }
    }
  };

  /* END CLASSIFICATION: free | step | corner | wall — CAD edge semantics
   * (a free end is a finished/visible edge; a wall end is not). */
  const classifyEnds = (runs) => {
    runs.forEach((r) => {
      if (!r.ends) r.ends = {};
      const W = wallsById.get(r.wallId);
      if (!r.ends.s0) r.ends.s0 = (W && r.s0 <= W.t / 2 + 0.06) ? "wall" : "free";
      if (!r.ends.s1) r.ends.s1 = (W && W.len - r.s1 <= W.t / 2 + 0.06) ? "wall" : "free";
    });
  };

  const wtSpecOf = (r) => surfaceSpecOf(surfaceMaterial(blob, "worktop", r.name), "worktop", null);
  const spSpecOf = (r) => surfaceSpecOf(surfaceMaterial(blob, "splash", r.name), "splash", null);

  const wtRuns = group((it) => it.wtName, { allowEmpty: true });
  const spRuns = group((it) => (it.splashOk ? it.bsName : ""));
  const wtCorners = connectCorners(wtRuns, (r) => r.depthM + wtSpecOf(r).overhangFrontMm / 1000);
  connectCorners(spRuns, (r) => spSpecOf(r).thicknessMm / 1000, wtCorners);
  markSteps(wtRuns);

  /* OVERRIDES RESOLVED ONCE PER DERIVED RUN, BEFORE ANY GEOMETRY PASS
   * (2026-09-12 user: "the backsplash goes outside of the wall"): the span an
   * elevation drag stored is applied HERE so the wall clamp, the tall-unit /
   * island clip and the window cuts below all have the last word over it.
   * One override drives at most ONE run — a run split in the middle used to
   * draw the old full span twice. */
  const claimOverrides = (kind, runs) => {
    const picks = runs.map((r) => resolveOverrideKey(store, surfaceKey(kind, r.wallId, r.members), r.members));
    const owner = new Map();                              // override key -> run index
    picks.forEach((pk, i) => {
      if (!pk) return;
      const j = owner.get(pk.key);
      if (j == null) { owner.set(pk.key, i); return; }
      const a = picks[j];
      // higher overlap keeps it; on a tie the run whose OWN key is the
      // override's key; a full tie keeps the earlier run (sorted wall, s0)
      const take = pk.score > a.score || (pk.score === a.score && pk.exact && !a.exact);
      if (take) { picks[j] = null; owner.set(pk.key, i); } else picks[i] = null;
    });
    return picks;
  };
  const spPicks = claimOverrides("splash", spRuns);
  spRuns.forEach((r, i) => {
    const pk = spPicks[i];
    r.over = pk ? pk.over : {};
    const os0 = num(r.over.s0, r.s0), os1 = num(r.over.s1, r.s1);
    // E4: an absolute span that no longer touches the cabinets it was drawn
    // on (they were moved) must not teleport the panel back to where they
    // stood — only the non-positional fields (height, thickness) survive
    if (Math.min(os1, r.s1) - Math.max(os0, r.s0) > 0.01) { r.s0 = os0; r.s1 = os1; }
  });

  /* ── THE WALL IS THE LIMIT (2026-08-19 user: "the worktop goes beyond the
   * wall and it's not accurate"). Every run is clamped into its wall's own
   * span; a corner-extended end keeps its reach (that IS the joint), so the
   * clamp allows the wall thickness of overhang at a corner end only. ── */
  const clampToWall = (runs) => {
    runs.forEach((r) => {
      const W = wallsById.get(r.wallId);
      if (!W) return;
      const ends = r.ends || {};
      const slack0 = ends.s0 === "corner" ? W.t : 0;
      const slack1 = ends.s1 === "corner" ? W.t : 0;
      r.s0 = Math.max(-slack0, r.s0);
      r.s1 = Math.min(W.len + slack1, r.s1);
      if (r.s1 - r.s0 < 0.02) r.dropped = true;              // clamped to nothing
    });
    return runs.filter((r) => !r.dropped);
  };

  /* ── OPENINGS ARE SOLID (2026-08-19 user: "the window … it goes on top of
   * it — make it smart"). A backsplash panel cannot cross a window or door:
   * each run is CUT around every opening whose height band overlaps the
   * panel, splitting it into the real remaining segments. The worktop is only
   * cut by openings that reach BELOW its top (a normal window sill sits above
   * the slab, so a standard kitchen is untouched). ── */
  const openingList = (opts && opts.openings) || [];
  const cutOpenings = (runs, bandOf, passThroughOnly) => {
    if (!openingList.length) return runs;
    const out = [];
    runs.forEach((r) => {
      const W = wallsById.get(r.wallId);
      const band = bandOf(r);                                 // { y0, y1 } world metres
      let segs = [{ s0: r.s0, s1: r.s1 }];
      openingList.forEach((o) => {
        if (!o || o.wallId !== r.wallId) return;
        const ow = num(o.width, 0.9);
        if (!(ow > 0)) return;
        const isDoor = String(o.type) === "door";
        const sill = isDoor ? 0 : num(o.sill, num(o.y, 0.9));
        const head = sill + num(o.height, isDoor ? 2.05 : 1.2);
        if (head <= band.y0 + 0.005 || sill >= band.y1 - 0.005) return;   // no vertical overlap
        /* PASS-THROUGH ONLY (2026-08-20 user: "fix the worktop — it's not
         * working like a smart thing"): a WINDOW whose sill merely dips into
         * the slab band sits ON the worktop — it must never cut the slab (a
         * sill at 900mm was deleting the whole run under it, which also
         * killed the corner joint). Only an opening reaching clearly BELOW
         * the slab (a door / floor glazing) actually interrupts a worktop. */
        if (passThroughOnly && sill > band.y0 - 0.05) return;
        const u0 = num(o.u, num(o.offset, 0)), u1 = u0 + ow;
        const next = [];
        segs.forEach((sg) => {
          if (u1 <= sg.s0 || u0 >= sg.s1) { next.push(sg); return; }      // clear of this opening
          if (u0 > sg.s0) next.push({ s0: sg.s0, s1: u0 });
          if (u1 < sg.s1) next.push({ s0: u1, s1: sg.s1 });
        });
        segs = next;
      });
      if (segs.length === 1 && segs[0].s0 === r.s0 && segs[0].s1 === r.s1) { out.push(r); return; }
      segs.forEach((sg, k) => {
        if (sg.s1 - sg.s0 < 0.05) return;                     // a sliver is not a panel
        const ends = { ...(r.ends || {}) };
        out.push({ ...r, s0: sg.s0, s1: sg.s1, ends, clippedByOpening: true, seg: k, wallLen: W ? W.len : null });
      });
    });
    return out;
  };

  let wtOut = clampToWall(wtRuns);
  let spOut = clampToWall(spRuns);
  // splash band = from the slab it sits on, up by its height
  spOut = cutOpenings(spOut, (r) => {
    const y0 = r.topM;
    return { y0, y1: y0 + spSpecOf(r).heightMm / 1000 };
  });
  // worktop band = the slab itself; ONLY a pass-through opening (door /
  // floor glazing) cuts it — a window on the worktop never does
  wtOut = cutOpenings(wtOut, (r) => ({ y0: r.topM, y1: r.topM + wtSpecOf(r).thicknessMm / 1000 }), true);
  classifyEnds(wtOut);
  classifyEnds(spOut);

  return {
    worktops: wtOut.map(mkWorktop),
    splashes: spOut.map(mkSplash).filter(Boolean),
    splashEligible,
  };
}

/** The slab top a splash sits on: the worktop covering this span, else the
 *  carcass top. Matches by SPAN OVERLAP (2026-08-18 — a single-point s0 test
 *  picked the wrong slab for trimmed/corner-extended runs); on a stepped wall
 *  each splash segment therefore sits on ITS OWN slab. */
export function worktopTopFor(splash, worktops) {
  let best = null, bestOv = 0.005;
  (worktops || []).forEach((w) => {
    if (w.wallId !== splash.wallId) return;
    const ov = Math.min(w.s1, splash.s1) - Math.max(w.s0, splash.s0);
    if (ov > bestOv) { bestOv = ov; best = w; }
  });
  return best ? best.topM + best.thicknessM : splash.topM;
}

/* ── QUANTITIES (the honest basis for money) ──────────────────────────────
 * Area is the real cut size; the visible edge is the front run plus any
 * exposed end. Both engines (JS + PY) price from these numbers. */
export function worktopQuantities(w) {
  const lengthM = Math.max(0, w.s1 - w.s0);
  const areaM2 = lengthM * w.depthM;
  // CAD edge semantics (2026-08-18): a FREE end is a finished/visible edge;
  // a step's taller side too. Wall/corner/step-lower ends are not edged.
  const ends = w.ends || { s0: "free", s1: "free" };
  let endCount = 0;
  ["s0", "s1"].forEach((k) => { if (ends[k] === "free") endCount += 1; });
  const edgeM = lengthM + endCount * w.depthM;
  return {
    lengthM: Math.round(lengthM * 1000) / 1000,
    areaM2: Math.round(areaM2 * 1000) / 1000,
    edgeM: Math.round(edgeM * 1000) / 1000,
    joints: (w.seams || []).length + (w.cornerJoints || 0) + (w.stepJoints || 0),
    cutouts: w.cutouts || 0,
  };
}
export function splashQuantities(s) {
  const lengthM = Math.max(0, s.s1 - s.s0);
  return {
    lengthM: Math.round(lengthM * 1000) / 1000,
    areaM2: Math.round(lengthM * s.heightM * 1000) / 1000,
  };
}

/* ── STORE HYGIENE (2026-09-12 "I can't delete the backsplash") ──────────────
 * Pure helpers over kitchenWizard.surfaceOverrides; the host calls them on
 * every splash edit / delete so a stale member-level entry can never survive
 * next to the merged-run entry it was folded into. */
const keyKind = (k) => String(k).split(":")[0];
const keyWall = (k) => String(k).split(":").slice(1, -1).join(":");

/** After an edit of splash run `editedKey` (members `editedMembers`) on
 *  `wallId`, every OTHER sp: entry on that wall whose members are all inside
 *  the edited run is superseded, and every sp: entry with no living member
 *  is dead. Worktop entries are never touched. */
export function purgeSplashOverrides(store, wallId, editedKey, editedMembers, livingIds) {
  const out = {};
  const edited = new Set(editedMembers || []);
  const alive = new Set(livingIds || []);
  Object.keys(store || {}).forEach((k) => {
    const o = store[k];
    if (keyKind(k) !== "sp" || keyWall(k) !== String(wallId) || k === editedKey) { out[k] = o; return; }
    const mem = (o && o.members) || [];
    if (mem.length && !mem.some((id) => alive.has(id))) return;       // dead
    if (mem.length && mem.every((id) => edited.has(id))) return;      // superseded
    out[k] = o;
  });
  return out;
}
/** every sp: entry that IS `key` or references any of `memberIds` */
export function dropSplashOverridesFor(store, key, memberIds) {
  const ids = new Set(memberIds || []);
  const out = {};
  Object.keys(store || {}).forEach((k) => {
    const o = store[k];
    if (keyKind(k) === "sp" && (k === key || ((o && o.members) || []).some((id) => ids.has(id)))) return;
    out[k] = o;
  });
  return out;
}
/** commit an elevation/3D edit; splash edits purge the wall first */
export function commitSurfaceEdit(state, key, members, patch) {
  if (!state) return;
  const kw = state.kitchenWizard || {};
  const cur = kw.surfaceOverrides || {};
  const living = (state.placements || []).map((p) => p && p.id).filter(Boolean);
  const base = keyKind(key) === "sp" ? purgeSplashOverrides(cur, keyWall(key), key, members, living) : cur;
  state.kitchenWizard = { ...kw, surfaceOverrides: { ...base, [key]: { ...(cur[key] || {}), members: (members || []).slice(), ...patch } } };
}
/** DELETE a splash run as drawn: its members lose their backsplash pick
 *  ("None" — the no-panel semantic BOTH pricers already honour: group() skips
 *  it, configOptionDeltas / config_option_deltas bill no OPT-BACKSPLASH for
 *  it — so nothing new crosses the JS≡PY line) and every splash override
 *  referencing them goes. Mutates in place (host convention). */
export function deleteSplashRun(state, key, members) {
  if (!state) return false;
  const ids = new Set(members || []);
  let touched = false;
  (state.placements || []).forEach((p) => {
    if (!p || !ids.has(p.id)) return;
    p.config = { ...(p.config || {}), backsplash: "None" };
    touched = true;
  });
  const kw = state.kitchenWizard || {};
  state.kitchenWizard = { ...kw, surfaceOverrides: dropSplashOverridesFor(kw.surfaceOverrides || {}, key, members) };
  return touched;
}
